import React, { useEffect, useState, useRef } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { GraduationCap, Users, Trophy, BookOpen, ArrowRight, TrendingUp } from 'lucide-react';
import { getNews, getAdmissionCount } from '../utils/api';

gsap.registerPlugin(ScrollTrigger);

const Home = () => {
    const [news, setNews] = useState([]);
    const [admissionCount, setAdmissionCount] = useState({ available_seats: 200 });
    const heroRef = useRef(null);
    const featuresRef = useRef(null);

    useEffect(() => {
        const fetchData = async () => {
            try {
                const [newsData, countData] = await Promise.all([
                    getNews(),
                    getAdmissionCount()
                ]);
                setNews(newsData.slice(0, 3));
                setAdmissionCount(countData);
            } catch (error) {
                console.error('Error fetching data:', error);
            }
        };
        fetchData();
    }, []);

    useEffect(() => {
        const sections = document.querySelectorAll('.animate-section');
        sections.forEach((section) => {
            gsap.fromTo(
                section,
                { opacity: 0, y: 50 },
                {
                    opacity: 1,
                    y: 0,
                    duration: 1,
                    scrollTrigger: {
                        trigger: section,
                        start: 'top 80%',
                        end: 'bottom 20%',
                        toggleActions: 'play none none reverse',
                    },
                }
            );
        });
    }, []);

    const stats = [
        { icon: Users, label: 'Students', value: '3600+' },
        { icon: GraduationCap, label: 'Faculty', value: '240+' },
        { icon: Trophy, label: 'Achievements', value: '500+' },
        { icon: BookOpen, label: 'Years of Excellence', value: '12+' },
    ];

    const features = [
        {
            title: 'Holistic Education',
            description: 'Comprehensive learning from K.G. to Junior College with Science, Commerce & Arts streams',
            image: 'images/1_100.jpg',
        },
        {
            title: 'Modern Infrastructure',
            description: 'State-of-the-art classrooms with audio-visual systems and advanced learning tools',
            image: 'images/1_98.jpg',
        },
        {
            title: 'Expert Faculty',
            description: 'Experienced and dedicated teachers committed to student success',
            image: 'images/1_102.jpg',
        },
        {
            title: 'Sports & Activities',
            description: 'Comprehensive sports facilities and extracurricular programs',
            image: 'images/1_68.jpeg',
        },
    ];

    return (
        <div className="min-h-screen" data-testid="home-page">
            {/* Hero Section */}
            <section
                ref={heroRef}
                className="relative h-screen flex items-center justify-center overflow-hidden"
                style={{
                    backgroundImage: `url(https://images.unsplash.com/photo-1600792170156-7fdc12ed6733?w=1920)`,
                    backgroundSize: 'cover',
                    backgroundPosition: 'center',
                    marginTop: '-2.5rem'
                }}
            >
                <div className="absolute inset-0 bg-gradient-to-br from-violet-900/90 via-purple-800/90 to-blue-900/90"></div>
                <div className="relative z-10 container mx-auto px-4 md:px-8 text-center text-white">
                    <motion.div
                        initial={{ opacity: 0, y: 30 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.8 }}
                    >
                        <h1 className="font-outfit font-bold text-4xl md:text-6xl lg:text-7xl mb-6 leading-tight text-white">
                            Welcome to R.K. English High School
                        </h1>
                        <p className="font-dm-sans text-lg md:text-2xl mb-8 text-white/95 max-w-3xl mx-auto">
                            Where Excellence Meets Education - Nurturing Young Minds Since 2014
                        </p>
                        <div className="flex flex-col sm:flex-row gap-4 justify-center">
                            <Link
                                to="/admissions"
                                className="inline-flex items-center justify-center px-10 py-5 bg-gradient-to-r from-red-600 to-red-700 hover:from-red-700 hover:to-red-800 text-white font-outfit font-bold text-lg rounded-full shadow-2xl hover:shadow-red-500/50 transition-all transform hover:scale-105 hover:-translate-y-1"
                                data-testid="hero-admission-button"
                            >
                                Apply Now
                                <ArrowRight className="ml-2 w-6 h-6" />
                            </Link>
                            <Link
                                to="/about"
                                className="inline-flex items-center justify-center px-10 py-5 bg-white/10 backdrop-blur-md hover:bg-white/20 text-white font-outfit font-bold text-lg rounded-full border-2 border-white/40 hover:border-white/60 shadow-lg transition-all transform hover:scale-105"
                                data-testid="hero-learn-more-button"
                            >
                                Learn More
                            </Link>
                        </div>
                    </motion.div>
                </div>
            </section>

            {/* Stats Section */}
            <section className="py-20 animate-section" style={{ background: 'linear-gradient(135deg, #C4122F 0%, #9F1025 100%)' }}>
                <div className="container mx-auto px-4 md:px-8">
                    <div className="grid grid-cols-2 lg:grid-cols-4 gap-8">
                        {stats.map((stat, index) => (
                            <motion.div
                                key={index}
                                className="text-center"
                                initial={{ opacity: 0, scale: 0.8 }}
                                whileInView={{ opacity: 1, scale: 1 }}
                                transition={{ delay: index * 0.1 }}
                            >
                                <stat.icon className="w-14 h-14 mx-auto mb-4 text-white" />
                                <div className="font-outfit font-bold text-5xl md:text-6xl text-white mb-2">{stat.value}</div>
                                <div className="font-dm-sans text-lg text-white/90 font-semibold">{stat.label}</div>
                            </motion.div>
                        ))}
                    </div>
                </div>
            </section>

            {/* About Preview */}
            <section className="py-20 bg-white animate-section">
                <div className="container mx-auto px-4 md:px-8">
                    <div className="grid lg:grid-cols-2 gap-12 items-center">
                        <div>
                            <h2 className="font-outfit font-bold text-4xl md:text-5xl text-red-600 mb-6">
                                About R.K. English High School
                            </h2>
                            <p className="font-dm-sans text-base md:text-lg text-slate-800 leading-relaxed mb-6">
                                R.K. English High School, a beacon of holistic education, caters to students from K.G. to Junior College.
                                Located just 8-10 minutes from Nallasopara East railway station, we offer comprehensive education up to 10th grade,
                                extending into Junior College with Science, Commerce, and Arts streams.
                            </p>
                            <p className="font-dm-sans text-base md:text-lg text-slate-800 leading-relaxed mb-8">
                                With a commitment to modern education techniques, we integrate audio-visual systems and software to foster
                                a dynamic and enriched learning environment.
                            </p>
                            <Link
                                to="/about"
                                className="inline-flex items-center justify-center px-8 py-4 bg-gradient-to-r from-red-600 to-red-700 hover:from-red-700 hover:to-red-800 text-white font-outfit font-bold rounded-full shadow-xl hover:shadow-2xl transition-all transform hover:scale-105 hover:-translate-y-1"
                                data-testid="about-explore-button"
                            >
                                Explore More
                                <ArrowRight className="ml-2 w-5 h-5" />
                            </Link>
                        </div>
                        <div className="rounded-3xl overflow-hidden shadow-2xl">
                            <img
                                src="images/aboutusimg.png"
                                alt="School Building"
                                className="w-full h-full object-cover"
                            />
                        </div>
                    </div>
                </div>
            </section>

            {/* Features Section */}
            <section ref={featuresRef} className="py-20 bg-white animate-section">
                <div className="container mx-auto px-4 md:px-8">
                    <div className="text-center mb-16">
                        <h2 className="font-outfit font-bold text-4xl md:text-5xl text-primary mb-4 text-black">Why Choose Us</h2>
                        <p className="font-dm-sans text-lg text-slate-600 max-w-2xl mx-auto">
                            Excellence in education with a perfect blend of academics, sports, and character building
                        </p>
                    </div>
                    <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
                        {features.map((feature, index) => (
                            <motion.div
                                key={index}
                                className="group relative overflow-hidden rounded-3xl bg-white shadow-sm hover:shadow-xl transition-all duration-300 border border-slate-100"
                                whileHover={{ y: -10 }}
                                data-testid={`feature-card-${index}`}
                            >
                                <div className="aspect-[4/3] overflow-hidden">
                                    <img
                                        src={feature.image}
                                        alt={feature.title}
                                        className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-110"
                                    />
                                </div>
                                <div className="p-6">
                                    <h3 className="font-outfit font-semibold text-xl mb-2">{feature.title}</h3>
                                    <p className="font-dm-sans text-sm text-slate-600">{feature.description}</p>
                                </div>
                            </motion.div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Admission Counter */}
            <section className="py-20 bg-gradient-to-r from-red-600 via-red-700 to-red-800 text-white animate-section">
                <div className="container mx-auto px-4 md:px-8 text-center">
                    <TrendingUp className="w-20 h-20 mx-auto mb-6 opacity-90" />
                    <h2 className="font-outfit font-bold text-4xl md:text-5xl mb-4">Limited Seats Available!</h2>
                    <p className="font-dm-sans text-xl md:text-2xl mb-10 text-white/95">Admissions Open for Academic Year 2026-2027</p>
                    <div className="inline-block bg-white/15 backdrop-blur-lg rounded-3xl p-10 mb-10 border border-white/20">
                        <div className="font-outfit font-bold text-7xl md:text-9xl mb-3 text-white">
                            {admissionCount.available_seats}
                        </div>
                        <div className="font-dm-sans text-xl text-white/90">Seats Remaining</div>
                    </div>
                    <div>
                        <Link
                            to="/admissions"
                            className="inline-flex items-center justify-center px-10 py-5 bg-white hover:bg-gray-50 text-red-700 font-outfit font-bold text-lg rounded-full shadow-2xl hover:shadow-white/20 transition-all transform hover:scale-105 hover:-translate-y-1"
                            data-testid="admission-counter-apply-button"
                        >
                            Apply Now - Don't Miss Out!
                        </Link>
                    </div>
                </div>
            </section>

            {/* Latest News */}
            <section className="py-20 bg-white animate-section">
                <div className="container mx-auto px-4 md:px-8">
                    <div className="flex items-center justify-between mb-12">
                        <h2 className="font-outfit font-bold text-4xl md:text-5xl text-red-600">Latest News</h2>
                        <Link
                            to="/news"
                            className="font-outfit font-semibold text-purple-700 hover:text-purple-900 flex items-center gap-2 transition-colors"
                            data-testid="view-all-news-link"
                        >
                            View All
                            <ArrowRight className="w-5 h-5" />
                        </Link>
                    </div>
                    <div className="grid md:grid-cols-3 gap-8">
                        {news.map((item, index) => (
                            <motion.div
                                key={item.id}
                                className="bg-white rounded-2xl p-6 shadow-sm hover:shadow-xl transition-all"
                                initial={{ opacity: 0, y: 20 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                transition={{ delay: index * 0.1 }}
                                data-testid={`news-card-${index}`}
                            >
                                <div className="text-sm text-muted-foreground mb-3">
                                    {new Date(item.date).toLocaleDateString('en-US', {
                                        year: 'numeric',
                                        month: 'long',
                                        day: 'numeric',
                                    })}
                                </div>
                                <h3 className="font-outfit font-bold text-xl mb-3">{item.title}</h3>
                                <p className="font-dm-sans text-slate-600 text-sm line-clamp-3">{item.content}</p>
                            </motion.div>
                        ))}
                    </div>
                </div>
            </section>

            {/* CTA Section */}
            <section className="py-32 bg-gradient-to-br from-purple-700 via-purple-800 to-purple-900 text-white animate-section">
                <div className="container mx-auto px-4 md:px-8 text-center">
                    <h2 className="font-outfit font-bold text-4xl md:text-6xl mb-8 text-white tracking-tight">
                        Ready to Join Us?
                    </h2>
                    <p className="font-dm-sans text-xl md:text-2xl mb-14 max-w-3xl mx-auto text-white font-medium leading-relaxed" style={{ opacity: 0.95 }}>
                        Start your child's journey towards excellence at R.K. English High School
                    </p>
                    <div className="flex flex-col sm:flex-row gap-6 justify-center">
                        <Link
                            to="/admissions"
                            className="inline-flex items-center justify-center px-10 py-5 bg-gradient-to-r from-red-600 to-red-700 hover:from-red-700 hover:to-red-800 text-white font-outfit font-bold text-lg rounded-full shadow-2xl hover:shadow-red-500/50 transition-all transform hover:scale-105 hover:-translate-y-1"
                            data-testid="cta-apply-button"
                        >
                            Apply for Admission
                        </Link>
                        <Link
                            to="/contact"
                            className="inline-flex items-center justify-center px-10 py-5 bg-transparent border-3 border-white hover:bg-white hover:text-purple-900 text-white font-outfit font-bold text-lg rounded-full transition-all transform hover:scale-105"
                            style={{ borderWidth: '3px' }}
                            data-testid="cta-contact-button"
                        >
                            Contact Us
                        </Link>
                    </div>
                </div>
            </section>
        </div>
    );
};

export default Home;